"use client";

import { JSONUIProvider, Renderer, defineRegistry } from "@json-render/react";
import { type ReactNode, createContext, useContext } from "react";
import { type FigureProps, type Spec, catalog } from "./catalog";

const ActivityContext = createContext<(id: string) => ReactNode>(() => null);
const ExerciseContext = createContext<(id: string) => ReactNode>(() => null);
// Hosts may typeset text (e.g. LaTeX on the Maths board); the default keeps plain text.
const TextContext = createContext<(text: string) => ReactNode>((text) => text);
const FigureContext = createContext<(props: FigureProps) => ReactNode>(() => null);
function HostText({ text }: { text: string }) {
  return useContext(TextContext)(text);
}
function HostFigure({ props }: { props: FigureProps }) {
  return useContext(FigureContext)(props);
}
const defaultHeading = (text: string) => <h2 className="stream-heading">{text}</h2>;
const HeadingContext = createContext<(text: string) => ReactNode>(defaultHeading);
function HostHeading({ text }: { text: string }) {
  return useContext(HeadingContext)(text);
}
const RevealContext = createContext<{
  report: (label: string, text: string, open: boolean) => void;
  isOpen: (label: string, text: string) => boolean;
}>({
  report: (_label: string, _text: string, _open: boolean) => {},
  isOpen: (_label: string, _text: string) => false,
});
function HostReveal({ label, text }: { label: string; text: string }) {
  const { report, isOpen } = useContext(RevealContext);
  return (
    <details
      className="stream-reveal"
      open={isOpen(label, text)}
      onToggle={(event) => report(label, text, event.currentTarget.open)}
    >
      <summary className="min-h-11 cursor-pointer py-3">
        <HostText text={label} />
      </summary>
      <p>
        <HostText text={text} />
      </p>
    </details>
  );
}
function HostExercise({ id }: { id: string }) {
  return useContext(ExerciseContext)(id);
}
function HostActivity({ id }: { id: string }) {
  return useContext(ActivityContext)(id);
}
const { registry } = defineRegistry(catalog, {
  components: {
    Stack: ({ children }) => <div className="stream-stack">{children}</div>,
    Panel: ({ children }) => <section className="stream-panel">{children}</section>,
    Heading: ({ props }) => <HostHeading text={String(props.text)} />,
    Text: ({ props }) => (
      <p className="stream-text whitespace-pre-wrap">
        <HostText text={String(props.text)} />
      </p>
    ),
    Table: ({ props }) => (
      <div className="stream-table overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr>
              {(props.headers as string[]).map((header, i) => (
                <th key={i}>
                  <HostText text={header} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(props.rows as string[][]).map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j}>
                    <HostText text={cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
    Reveal: ({ props }) => <HostReveal label={String(props.label)} text={String(props.text)} />,
    Activity: ({ props }) => <HostActivity id={String(props.activityId)} />,
    Exercise: ({ props }) => <HostExercise id={String(props.exerciseId)} />,
    Figure: ({ props }) => <HostFigure props={props as FigureProps} />,
  },
});

export function StreamingRenderer({
  spec,
  activity,
  exercise = () => null,
  reveal = () => {},
  revealOpen = () => false,
  heading = defaultHeading,
  text = (value: string) => value,
  figure = () => null,
}: {
  spec: Spec;
  activity: (id: string) => ReactNode;
  exercise?: (id: string) => ReactNode;
  reveal?: (label: string, text: string, open: boolean) => void;
  revealOpen?: (label: string, text: string) => boolean;
  heading?: (text: string) => ReactNode;
  text?: (text: string) => ReactNode;
  figure?: (props: FigureProps) => ReactNode;
}) {
  return (
    <HeadingContext.Provider value={heading}>
      <ActivityContext.Provider value={activity}>
        <ExerciseContext.Provider value={exercise}>
          <TextContext.Provider value={text}>
            <FigureContext.Provider value={figure}>
              <RevealContext.Provider value={{ report: reveal, isOpen: revealOpen }}>
                <JSONUIProvider registry={registry}>
                  <Renderer spec={spec} registry={registry} />
                </JSONUIProvider>
              </RevealContext.Provider>
            </FigureContext.Provider>
          </TextContext.Provider>
        </ExerciseContext.Provider>
      </ActivityContext.Provider>
    </HeadingContext.Provider>
  );
}
